import React, { useState, useEffect } from 'react'
import { 
  Calendar,
  DollarSign,
  CheckCircle,
  XCircle,
  Clock,
  AlertCircle,
  Edit,
  Trash2,
  ArrowLeft
} from 'lucide-react'

interface PlanoServico {
  id: string
  servico_id: string
  valor_customizado?: number
  servicos?: {
    nome: string
    descricao?: string
    valor_padrao: number
  }
}

interface CondicaoEspecial {
  id: string
  mes_inicio: number
  mes_fim: number
  tipo_condicao: string
  valor: number
  descricao?: string
}

interface Plano {
  id: string
  nome: string
  tipo_venda?: string
  data_inicio: string
  data_fim?: string
  dia_vencimento: number
  valor_base: number
  desconto_geral_tipo?: string
  desconto_geral_valor?: number
  status: string
  observacoes?: string
  created_at: string
  plano_servicos?: PlanoServico[]
  plano_condicoes_especiais?: CondicaoEspecial[]
}

interface ClientePlanosProps {
  clienteId: string
}

const ClientePlanos: React.FC<ClientePlanosProps> = ({ clienteId }) => {
  const [planos, setPlanos] = useState<Plano[]>([])
  const [loading, setLoading] = useState(true)
  const [viewMode, setViewMode] = useState<'list' | 'view' | 'edit'>('list')
  const [selectedPlano, setSelectedPlano] = useState<Plano | null>(null)

  useEffect(() => {
    fetchPlanos()
  }, [clienteId])

  const fetchPlanos = async () => {
    setLoading(true)
    try {
      const response = await fetch('/api/planos')
      const data = await response.json()
      
      if (data.data) {
        // Filtrar planos do cliente específico
        const planosCliente = data.data.filter((plano: any) => plano.cliente_id === clienteId)
        setPlanos(planosCliente)
      }
    } catch (error) {
      console.error('Erro ao carregar planos:', error)
    } finally {
      setLoading(false)
    }
  }

  const formatCurrency = (value: number) => {
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL'
    }).format(value || 0)
  }

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('pt-BR')
  }

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'ativo':
        return <CheckCircle className="w-4 h-4 text-green-600" />
      case 'pausado':
        return <Clock className="w-4 h-4 text-yellow-600" />
      case 'cancelado':
        return <XCircle className="w-4 h-4 text-red-600" />
      case 'finalizado':
        return <AlertCircle className="w-4 h-4 text-gray-600" />
      default:
        return <AlertCircle className="w-4 h-4 text-gray-600" />
    }
  }

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'ativo':
        return 'bg-green-100 text-green-800'
      case 'pausado':
        return 'bg-yellow-100 text-yellow-800'
      case 'cancelado':
        return 'bg-red-100 text-red-800'
      case 'finalizado':
        return 'bg-gray-100 text-gray-800'
      default:
        return 'bg-gray-100 text-gray-800'
    }
  }

  const handleViewPlano = (planoId: string) => {
    console.log('🔍 handleViewPlano chamado - VERSÃO ATUALIZADA', planoId)
    const plano = planos.find(p => p.id === planoId)
    if (plano) {
      setSelectedPlano(plano)
      setViewMode('view')
      console.log('✅ Plano selecionado, mudando para modo view')
    }
  }

  const handleEditPlano = (planoId: string) => {
    console.log('✏️ handleEditPlano chamado - VERSÃO ATUALIZADA', planoId)
    const plano = planos.find(p => p.id === planoId)
    if (plano) {
      setSelectedPlano(plano)
      setViewMode('edit')
      console.log('✅ Plano selecionado, mudando para modo edit')
    }
  }

  const handleBackToList = () => {
    setViewMode('list')
    setSelectedPlano(null)
  }

  const handleDeletePlano = async (planoId: string) => {
    if (!confirm('Tem certeza que deseja excluir este plano?')) {
      return
    }

    try {
      const response = await fetch(`/api/planos?id=${planoId}`, {
        method: 'DELETE'
      })

      if (response.ok) {
        await fetchPlanos() // Recarregar lista
      } else {
        const result = await response.json()
        alert('Erro ao excluir plano: ' + result.error)
      }
    } catch (error) {
      console.error('Erro ao excluir plano:', error)
      alert('Erro ao excluir plano')
    }
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center py-8">
        <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-orange-600"></div>
        <span className="ml-2 text-gray-600">Carregando planos...</span>
      </div>
    )
  }

  // Renderizar visualização detalhada do plano
  if (viewMode === 'view' && selectedPlano) {
    return (
      <div className="space-y-4">
        <button
          onClick={handleBackToList}
          className="flex items-center text-orange-600 hover:text-orange-700 transition-colors mb-4"
        >
          <ArrowLeft className="w-4 h-4 mr-2" />
          Voltar para lista de planos
        </button>

        <div className="bg-white rounded-lg border border-gray-200 p-6">
          <div className="flex items-center justify-between mb-6">
            <div>
              <h3 className="text-2xl font-bold text-gray-900">{selectedPlano.nome}</h3>
              {selectedPlano.tipo_venda && (
                <span className="inline-block mt-1 px-2 py-1 text-xs font-medium bg-blue-100 text-blue-800 rounded">
                  {selectedPlano.tipo_venda === 'plano_recorrente' ? 'Plano Recorrente' : 'Venda Avulsa'}
                </span>
              )}
            </div>
            <span className={`inline-flex items-center px-3 py-1 rounded-full text-sm font-medium ${getStatusColor(selectedPlano.status)}`}>
              {getStatusIcon(selectedPlano.status)}
              <span className="ml-1 capitalize">{selectedPlano.status}</span>
            </span>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Data de Início</label>
                <div className="flex items-center text-gray-900">
                  <Calendar className="w-5 h-5 mr-2 text-gray-400" />
                  {formatDate(selectedPlano.data_inicio)}
                </div>
              </div>

              {selectedPlano.data_fim && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Data de Término</label>
                  <div className="flex items-center text-gray-900">
                    <Calendar className="w-5 h-5 mr-2 text-gray-400" />
                    {formatDate(selectedPlano.data_fim)}
                  </div>
                </div>
              )}

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Dia de Vencimento</label>
                <div className="flex items-center text-gray-900">
                  <Clock className="w-5 h-5 mr-2 text-gray-400" />
                  Dia {selectedPlano.dia_vencimento} de cada mês
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Data de Criação</label>
                <div className="flex items-center text-gray-900">
                  <Calendar className="w-5 h-5 mr-2 text-gray-400" />
                  {formatDate(selectedPlano.created_at)}
                </div>
              </div>
            </div>

            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Valor Base</label>
                <div className="flex items-center text-gray-900">
                  <DollarSign className="w-5 h-5 mr-2 text-gray-400" />
                  <span className="text-2xl font-bold text-green-600">
                    {formatCurrency(selectedPlano.valor_base)}
                  </span>
                </div>
              </div>

              {selectedPlano.desconto_geral_tipo && selectedPlano.desconto_geral_valor && selectedPlano.desconto_geral_valor > 0 && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Desconto Geral</label>
                  <div className="flex items-center text-orange-600">
                    <DollarSign className="w-5 h-5 mr-2" />
                    <span className="text-lg font-semibold">
                      {selectedPlano.desconto_geral_tipo === 'percentual' 
                        ? `${selectedPlano.desconto_geral_valor}%` 
                        : formatCurrency(selectedPlano.desconto_geral_valor)}
                    </span>
                  </div>
                </div>
              )}
            </div>
          </div>

          {selectedPlano.plano_servicos && selectedPlano.plano_servicos.length > 0 && (
            <div className="mb-6">
              <label className="block text-sm font-medium text-gray-700 mb-3">Serviços Contratados</label>
              <div className="bg-gray-50 rounded-lg border border-gray-200 overflow-hidden">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-100">
                    <tr>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-700 uppercase">Serviço</th>
                      <th className="px-4 py-3 text-right text-xs font-medium text-gray-700 uppercase">Valor</th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {selectedPlano.plano_servicos.map((ps) => (
                      <tr key={ps.id}>
                        <td className="px-4 py-3">
                          <div className="text-sm font-medium text-gray-900">
                            {ps.servicos?.nome || 'Serviço'}
                          </div>
                          {ps.servicos?.descricao && (
                            <div className="text-xs text-gray-500 mt-1">{ps.servicos.descricao}</div>
                          )}
                        </td>
                        <td className="px-4 py-3 text-right text-sm text-gray-900 font-medium">
                          {formatCurrency(ps.valor_customizado || ps.servicos?.valor_padrao || 0)}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {selectedPlano.plano_condicoes_especiais && selectedPlano.plano_condicoes_especiais.length > 0 && (
            <div className="mb-6">
              <label className="block text-sm font-medium text-gray-700 mb-3">Condições Especiais</label>
              <div className="space-y-3">
                {selectedPlano.plano_condicoes_especiais.map((condicao) => (
                  <div key={condicao.id} className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <div className="flex items-center space-x-2 mb-2">
                          <span className="text-sm font-medium text-gray-900">
                            Mês {condicao.mes_inicio} {condicao.mes_fim !== condicao.mes_inicio && `até ${condicao.mes_fim}`}
                          </span>
                          <span className="px-2 py-0.5 text-xs font-medium bg-yellow-200 text-yellow-800 rounded">
                            {condicao.tipo_condicao === 'desconto' ? 'Desconto' : 
                             condicao.tipo_condicao === 'acrescimo' ? 'Acréscimo' : 
                             condicao.tipo_condicao === 'isencao' ? 'Isenção' : condicao.tipo_condicao}
                          </span>
                        </div>
                        {condicao.descricao && (
                          <p className="text-sm text-gray-700">{condicao.descricao}</p>
                        )}
                      </div>
                      <div className="ml-4 text-right">
                        <span className="text-lg font-bold text-yellow-700">
                          {condicao.tipo_condicao === 'isencao' ? 'Grátis' : 
                           condicao.tipo_condicao === 'valor_fixo' ? formatCurrency(condicao.valor) :
                           `${condicao.valor}%`}
                        </span>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {selectedPlano.observacoes && (
            <div className="mb-6">
              <label className="block text-sm font-medium text-gray-700 mb-2">Observações</label>
              <div className="bg-gray-50 rounded-lg p-4 border border-gray-200">
                <p className="text-gray-700 whitespace-pre-wrap">{selectedPlano.observacoes}</p>
              </div>
            </div>
          )}

          <div className="flex justify-end space-x-3 mt-6 pt-6 border-t border-gray-200">
            <button
              onClick={() => handleEditPlano(selectedPlano.id)}
              className="flex items-center px-4 py-2 bg-orange-600 text-white rounded-lg hover:bg-orange-700 transition-colors"
            >
              <Edit className="w-4 h-4 mr-2" />
              Editar Plano
            </button>
            <button
              onClick={() => {
                if (confirm('Tem certeza que deseja excluir este plano?')) {
                  handleDeletePlano(selectedPlano.id)
                  handleBackToList()
                }
              }}
              className="flex items-center px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors"
            >
              <Trash2 className="w-4 h-4 mr-2" />
              Excluir Plano
            </button>
          </div>
        </div>
      </div>
    )
  }

  // Renderizar modo de edição (placeholder - pode ser implementado depois)
  if (viewMode === 'edit' && selectedPlano) {
    return (
      <div className="space-y-4">
        <button
          onClick={handleBackToList}
          className="flex items-center text-orange-600 hover:text-orange-700 transition-colors mb-4"
        >
          <ArrowLeft className="w-4 h-4 mr-2" />
          Voltar para lista de planos
        </button>

        <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
          <p className="text-yellow-800">
            Modo de edição em desenvolvimento. Por enquanto, edite o plano através do menu principal 
            <span className="font-mono bg-yellow-100 px-1 rounded ml-1">Planos</span>.
          </p>
        </div>
      </div>
    )
  }

  if (planos.length === 0) {
    return (
      <div className="text-center py-8">
        <div className="w-16 h-16 mx-auto mb-4 bg-gray-100 rounded-full flex items-center justify-center">
          <Calendar className="w-8 h-8 text-gray-400" />
        </div>
        <h3 className="text-lg font-medium text-gray-900 mb-2">Nenhuma venda encontrada</h3>
        <p className="text-gray-500 mb-4">Este cliente ainda não possui vendas cadastradas.</p>
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
          <div className="flex items-start">
            <div className="text-blue-600 mr-2 mt-0.5">ℹ️</div>
            <div>
              <p className="text-sm text-blue-800">
                <strong>Para criar vendas:</strong> Acesse o menu 
                <span className="font-mono bg-blue-100 px-1 rounded ml-1">Planos → Nova Venda</span>
              </p>
            </div>
          </div>
        </div>
      </div>
    )
  }

  return (
    <div className="space-y-4">
      {planos.map((plano) => (
        <div key={plano.id} className="bg-gray-50 rounded-lg p-4 border border-gray-200">
          <div className="flex items-start justify-between">
            <div className="flex-1">
              <div className="flex items-center space-x-2 mb-2">
                <h4 className="text-lg font-semibold text-gray-900">{plano.nome}</h4>
                <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getStatusColor(plano.status)}`}>
                  {getStatusIcon(plano.status)}
                  <span className="ml-1 capitalize">{plano.status}</span>
                </span>
              </div>
              
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-sm text-gray-600">
                <div className="flex items-center">
                  <Calendar className="w-4 h-4 mr-2" />
                  <span>Início: {formatDate(plano.data_inicio)}</span>
                </div>
                
                {plano.data_fim && (
                  <div className="flex items-center">
                    <Calendar className="w-4 h-4 mr-2" />
                    <span>Fim: {formatDate(plano.data_fim)}</span>
                  </div>
                )}
                
                <div className="flex items-center">
                  <DollarSign className="w-4 h-4 mr-2" />
                  <span>{formatCurrency(plano.valor_base)}/mês</span>
                </div>
              </div>
              
              <div className="mt-2 text-sm text-gray-600">
                <span>Vencimento: dia {plano.dia_vencimento} de cada mês</span>
              </div>
              
              {plano.observacoes && (
                <div className="mt-3 p-3 bg-white rounded border border-gray-200">
                  <p className="text-sm text-gray-700">{plano.observacoes}</p>
                </div>
              )}
            </div>
            
            <div className="flex space-x-2 ml-4">
              <button
                onClick={() => handleViewPlano(plano.id)}
                className="text-blue-600 hover:text-blue-900 transition-colors"
                title="Visualizar plano"
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                </svg>
              </button>
              <button
                onClick={() => handleEditPlano(plano.id)}
                className="text-orange-600 hover:text-orange-900 transition-colors"
                title="Editar plano"
              >
                <Edit className="w-4 h-4" />
              </button>
              <button
                onClick={() => handleDeletePlano(plano.id)}
                className="text-red-600 hover:text-red-900 transition-colors"
                title="Excluir plano"
              >
                <Trash2 className="w-4 h-4" />
              </button>
            </div>
          </div>
        </div>
      ))}
    </div>
  )
}

export default ClientePlanos
